import React, { useCallback } from "react";
import { EntityChipShell, useIsMobile, type Color } from "@salestrak/ui";

import {
  entityDataEqual,
  resolveEntityLabel,
  resolveEntityVisual,
  type EntityData,
} from "#app/lib/entity-visuals";

import { EntityTagBody } from "./entity-tag-body";
import { EntityPreview } from "./entity-preview";
import { EntityVisual, type EntityVisualSize } from "./entity-visual";
import { useEntityDetailOptional } from "./entity-detail-provider";
import type { Omittable } from "@salestrak/ui";

// Chips are props-only — preview/dialog hooks mount lazily on open.
// `interactive` defaults TRUE; pass `interactive={false}` when the parent owns
// the click.

export type EntityTagProps = Omittable<{
  entity: EntityData;
  variant?: "plain" | "chip";
  color?: Color;
  display?: "full" | "visual" | "label";
  weight?: "regular" | "medium" | "semibold";
  size?: EntityVisualSize;
  subtitle?: boolean;
  /** Secondary line rendered below the label (e.g. a search result's address). */
  description?: string;
  /** The SalesTrak author sentinel; ⇒ never interactive. */
  system?: boolean;
  interactive?: boolean;
  onClick?: (e?: React.MouseEvent<HTMLElement>) => void;
  /** Anchored confirm popover before clicks that navigate away. */
  confirmOnClick?: {
    message: string;
    confirmLabel?: string;
    cancelLabel?: string;
  };
  as?: "box" | "text";
  padded?: boolean;
  /** Ghost hover wash; always forced off for `display="label"`. */
  ghost?: boolean;
  /** Force-mute the label (e.g. de-emphasized selector trigger values). */
  mutedLabel?: boolean;
}>;

function EntityTagComponent({
  entity,
  variant = "plain",
  color,
  display = "full",
  weight,
  size = "sm",
  subtitle,
  description,
  system,
  interactive = true,
  onClick,
  confirmOnClick,
  as = "box",
  padded,
  ghost = true,
  mutedLabel,
}: EntityTagProps) {
  const detail = useEntityDetailOptional();
  const isMobile = useIsMobile();

  const isInteractive = interactive && !system;

  const resolvedWeight = weight ?? (display === "label" ? "semibold" : "medium");
  const ghostEnabled = display === "label" ? false : ghost;
  const label = system ? "SalesTrak" : resolveEntityLabel(entity);

  const defaultClick =
    isInteractive && !onClick && detail
      ? () => detail.openEntity(entity.kind, entity.id)
      : undefined;
  const clickAction = isInteractive ? (onClick ?? defaultClick) : undefined;

  const clickConfirmation =
    confirmOnClick && clickAction ? confirmOnClick : undefined;

  const handleClick = useCallback(
    (e: React.MouseEvent<HTMLElement>) => {
      // Interactive tags live inside clickable rows/cards — never double-fire.
      e?.stopPropagation?.();
      clickAction?.(e);
    },
    [clickAction],
  );

  const subtitleText =
    subtitle && entity.kind === "user"
      ? (entity.user?.username ??
        (entity.user?.name ? (entity.user?.email ?? null) : null))
      : null;

  // Derive label muting from the same resolved visual the avatar dims on, so
  // text and avatar can't disagree about a user's suspended state.
  const entityVisual = resolveEntityVisual(entity);
  const resolvedMutedLabel =
    mutedLabel || (entityVisual.type === "avatar" && entityVisual.suspended);

  const visual =
    display === "label" ? undefined : (
      <EntityVisual
        entity={entity}
        size={size}
        padded={padded}
        system={system}
      />
    );

  const renderPreview = isInteractive
    ? () => <EntityPreview entity={entity} />
    : undefined;

  return (
    <EntityChipShell
      variant={variant}
      color={color}
      as={as}
      disableHover={isMobile}
      disabled={!isInteractive}
      onClick={clickAction ? handleClick : undefined}
      confirmOnClick={clickConfirmation}
      renderPreview={renderPreview}
      ghost={ghostEnabled}
      // Small chips tighten the visual↔label gap so it stays proportional to
      // the glyph — derived here once, never overridden per call site.
      visualGap={size === "2xs" || size === "xs" ? "xs" : "sm"}
    >
      <EntityTagBody
        label={label}
        visual={visual}
        display={display}
        weight={resolvedWeight}
        size={size}
        subtitle={subtitleText}
        description={description}
        mutedLabel={resolvedMutedLabel}
        as={as}
      />
    </EntityChipShell>
  );
}

EntityTagComponent.displayName = "EntityTag";

// `entity` is value-compared structurally because call sites pass fresh inline
// objects every render; all other props are reference-compared.
function propsEqual(prev: EntityTagProps, next: EntityTagProps): boolean {
  if (!entityDataEqual(prev.entity, next.entity)) return false;

  const prevKeys = Object.keys(prev) as (keyof EntityTagProps)[];
  const nextKeys = Object.keys(next) as (keyof EntityTagProps)[];
  // Equal key counts plus every prev key matching covers a key present on only
  // one side, without allocating a Set per comparison.
  if (prevKeys.length !== nextKeys.length) return false;
  for (const key of prevKeys) {
    if (key === "entity") continue;
    if (!Object.is(prev[key], next[key])) return false;
  }
  return true;
}

export const EntityTag = React.memo(EntityTagComponent, propsEqual);
